import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MailIcon from "@mui/icons-material/Mail";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

import Collapse from "@mui/material/Collapse";

import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import TransformOutlinedIcon from '@mui/icons-material/TransformOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AutoModeOutlinedIcon from '@mui/icons-material/AutoModeOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import ModelTrainingOutlinedIcon from '@mui/icons-material/ModelTrainingOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import "./sidebar.scss";
// import { makeStyles } from '@mui/material';
import { makeStyles } from "@mui/styles";
// import ProjectContext from "@context/Project/projectContext";

const useStyles: any = makeStyles({
  root: {
    "&$selected": {
      backgroundImage: "linear-gradient(to right, #f04a23 , #ed8623a1)",
      borderBottomRightRadius: "25px",
      borderTopRightRadius: "25px",
      "&:hover": {
        // backgroundColor: "tomato"
      },
      "& .MuiListItemIcon-root": {
        color: "white", // Change icon color when selected
      },
      "& .MuiListItemText-root": {
        color: "white", // Change text color when selected
        fontWeight: "bold", // Make the text bold when selected
      }
    },
  },
  selected: {},
});

const drawerWidth = 240;

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
} 


const getIcon = (name: string) => {
  switch(name) {
    case 'data-labelling':
      return <LabelOutlinedIcon/>  
    case 'diverse-selection':
      return <PublicOutlinedIcon />
    case 'augmentation':
      return <TransformOutlinedIcon />
    case 'auto-labelling':
      return <AutoModeOutlinedIcon />
    case 'training':
      return <SchoolOutlinedIcon />
    case 'evaluation':
      return <AssessmentOutlinedIcon />
    case 'active-selection':
      return <SearchOutlinedIcon />
    case 'target-selection':
      return <TrackChangesOutlinedIcon />
    case 'inference':
      return <PsychologyOutlinedIcon />
    case 'relevant-data-selection':
      return <InsightsOutlinedIcon />
    case 'export':
      return <FileUploadOutlinedIcon />
    case 'export-ct-instances':
      return <FileUploadOutlinedIcon />
    case 'logs':
      return <HistoryOutlinedIcon />
    case 'upload-data':
      return <CloudUploadOutlinedIcon />
    case 'labels':
      return <BookmarkBorderOutlinedIcon />
    case 'clarifier':
      return <StackedBarChartOutlinedIcon />
    case 'initial-selection':
      return <FilterAltOutlinedIcon />
    case 'slice-marking':
      return <LayersOutlinedIcon />
    case 'auto-labelling-correction':
      return <BuildOutlinedIcon />
    case 'tiered-hardness':
      return <StackedBarChartOutlinedIcon />
    case 'model-al':
      return <ModelTrainingOutlinedIcon />
    case 'medsam-2-propagation':
      return <ScienceOutlinedIcon />
    case '1':
      return <ScienceOutlinedIcon />
    case '12':
      return <GetAppOutlinedIcon />
    case 'validation-logs':
      return <CheckCircleOutlinedIcon />
    case 'clarifier-history':
      return <HistoryOutlinedIcon />
    case 'propagated-slices-correction':
      return <EditOutlinedIcon />
    case '2d-model':
      return <SchoolOutlinedIcon />
    case '3d-model':
      return <ModelTrainingOutlinedIcon />
    default:
      return <MailIcon />
  }
}



export default function Sidebar(props: Props) {
  
  // const projectContextInfo = React.useContext(ProjectContext);
  // const { state: projectState } = projectContextInfo;

  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const navigate = useNavigate();

  const { projectId, project_type } = useParams();
  
  let objectDetectionOptions = [
    { name: "Data Labelling", value: "data-labelling" },
    {
      name: 'Diverse Selection',
      value: 'diverse-selection' 
    },
    { name: "Augmentation", value: "augmentation" },
    { name: "Auto Labelling", value: "auto-labelling" },
    { name: "Training", value: "training" },
    {
      name: "Evaluation",
      value: "evaluation",
      children: [
        { name: "Home", value: "evaluation" },
        { name: "Logs", value: "logs" },
      ],
    },
    {
      name: "Tiered Hardness",
      value: "clarifier",
      children: [
        { name: "Start New Round", value: "clarifier" },
        { name: "History", value: "history" },
      ],
    },
    {
      name: "Active Selection",
      value: "active-selection",
      children: [
        { name: "Home", value: "active-selection" },
        { name: "History", value: "history" },
      ],
    },
    {
      name: "Target Selection",
      value: "target-selection",
      children: [
        { name: "Home", value: "target-selection" },
        { name: "History", value: "history" },
      ],
    },
    { name: "Relevant Data Selection", value: "relevant-data-selection" },
    { name: "Export", value: "export" },
  ];
  
  let imageClassificationOptions = [
    { name: "Data Labelling", value: "data-labelling" },
    { 
      name: 'Diverse Selection',
      value: 'diverse-selection' 
    },
    { name: "Augmentation", value: "augmentation" },
    { name: "Auto Labelling", value: "auto-labelling" },
    {
      name: "Tiered Hardness",
      value: "clarifier",
      children: [
        { name: "Start New Round", value: "clarifier" },
        { name: "History", value: "history" },
      ],
    },
    {
      name: "Active Selection",
      value: "active-selection",
      children: [
        { name: "Home", value: "active-selection" },
        { name: "History", value: "history" },
      ],
    },
    {
      name: "Target Selection",
      value: "target-selection",
      children: [
        { name: "Home", value: "target-selection" },
        { name: "History", value: "history" },
      ],
    },
    { name: "Training", value: "training" },
    {
      name: "Evaluation",
      value: "evaluation",
      children: [
        { name: "Home", value: "evaluation" },
        { name: "Logs", value: "logs" },
      ],
    },
    { name: "Relevant Data Selection", value: "relevant-data-selection" },
    { name: "Export", value: "export" },
  ];

  let niftiFileOptions = [
    // { name: "NIFTI Files", value: "nifti-files" },
    {
      name: "Tiered Hardness",
      value: "tiered-hardness",
      children: [
        { name: "Start New Round", value: "tiered-hardness" },
      ],
    },
    { name: "Modal AL", value: "model-al" },
    { name: "Slice Marking", value: "slice-marking" },
    { name: "Auto-Labelling Correction", value: "auto-labelling-correction" },
    { name: "MedSam 2 Propagation", value: "medsam-2-propagation" },
    { name: "Propagated Slices Correction", value: "propagated-slices-correction" },
    { name: "Export CT Instances", value: "export-ct-instances" },
    { name: "Validation logs", value: "validation-logs" },
    {
      name: "2D Model",
      value: "2d-model",
      children: [
        { name: "New Training", value: "new-training" },
      ],
    },
    {
      name: "3D Model",
      value: "3d-model",
      children: [
        { name: "New Training", value: "new-training" },
        { name: "Ground-Truth Evaluation", value: "ground-truth-evaluation" },
      ],
    },
  ];
  
  
  // let instanceSegmentationClarifierOptions = [
  //   { name: "Data Labelling", value: "data-labelling" },
  //   { 
  //     name: 'Diverse Selection',
  //     value: 'diverse-selection' 
  //   },
  //   { name: "Augmentation", value: "augmentation" },
  //   { name: "Auto Labelling", value: "auto-labelling" },
  //   {
  //     name: "Tiered Hardness",
  //     value: "clarifier",
  //     children: [
  //       { name: "Start New Round", value: "clarifier" },
  //       { name: "History", value: "history" },
  //     ],
  //   },
  //   {
  //     name: "Active Selection",
  //     value: "active-selection",
  //     children: [
  //       { name: "Home", value: "active-selection" },
  //       { name: "History", value: "history" },
  //     ],
  //   },
  //   { name: "Training", value: "training" },
  //   {
  //     name: "Evaluation",
  //     value: "evaluation",
  //     children: [
  //       { name: "Home", value: "evaluation" },
  //       { name: "Logs", value: "logs" },
  //     ],
  //   },
  //   { name: "Export", value: "export" },
  // ];
  
  
  if(project_type === 'image_classification_-_(clarifier)' || project_type === 'object_detection_-_(clarifier)' || project_type === 'instance_segmentation_-_(clarifier)' || project_type === 'semantic_segmentation_-_(clarifier)' || project_type === '3d-ct_segmentation_-_(clarifier)') {
    objectDetectionOptions[1].name = 'Initial Selection'
    objectDetectionOptions[1].value = 'initial-selection'
    imageClassificationOptions[1].name = 'Initial Selection'
    imageClassificationOptions[1].value = 'initial-selection'
    
    // Below we are injecting the inference page for object detection clarifier and instance segmentation clarifier
    if(project_type !== 'image_classification_-_(clarifier)') {
      const inferenceSidebarObj = {
        name: "Inference",
        value: "inference",
        children: [
          { name: "Perform Inference", value: "inference" },
          { name: "Run History", value: "logs" },
        ],
      }
      imageClassificationOptions.splice(9,0,inferenceSidebarObj)
    }
    imageClassificationOptions = imageClassificationOptions.filter(x => x.value !== 'active-selection') // We want to remove the active selection 
  }
  
  // Remove the clarifier option from the side bar for object detection and image classification
  if(project_type === 'object_detection' || project_type === 'image_classification') {
    objectDetectionOptions = objectDetectionOptions.filter(x => x.value !== 'clarifier')
    imageClassificationOptions = imageClassificationOptions.filter(x => x.value !== 'clarifier') 
  }

  let options =
    project_type === "object_detection"
      ? objectDetectionOptions
      : imageClassificationOptions;

  if(project_type === '3d-ct_segmentation_-_(clarifier)') {
    options = niftiFileOptions;
  }
      
  
  if (isNaN(Number(projectId))) {
    navigate("/projects");
  }

  const currentPage = useLocation()
    .pathname.split("/")[4]
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

  const currentUrl = useLocation().pathname.split("/")[4];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRedirect = (pathname: string, parentItem: any) => {
    // if(project_type === 'instance_segmentation_-_(clarifier)' &&  pathname === 'data-labelling') return globalThis.window.location.href = `/segmentation?project_id=${projectId}`
    // The below if is for so we dont navigate to /test/test
    if (parentItem?.value === pathname) parentItem = false;
    
    // Special handling for Propagated slices correction - navigate to MedSAM 2 Propagation list
    // if (pathname === 'propagated-slices-correction') {
    //   navigate(`/${project_type}/operations/${projectId}/medsam-2-propagation/list`);
    //   setMobileOpen(false);
    //   return;
    // }
    
    const url = parentItem
      ? `/${project_type}/operations/${projectId}/${parentItem?.value}/${pathname}`
      : `/${project_type}/operations/${projectId}/${pathname}`;
    navigate(url);
    setMobileOpen(false);
  };

  const styles = useStyles();

  const MyNestedList = ({
    items,
    handleRedirect,
    level,
    isChildrenElement = false,
    parentItem,
  }: any) => {
    // For 3d-ct_segmentation_-_(clarifier), open "Tiered Hardness" (index 0) by default
    const defaultOpenIndex = project_type === '3d-ct_segmentation_-_(clarifier)' && level === 0 ? 0 : null;
    const [open, setOpen] = React.useState<number | null>(defaultOpenIndex);

    const handleClick = (index: number) => {
      setOpen(index === open ? null : index);
    };
    return (
      <>
        {items.map((item: any, index: any) => (
          <React.Fragment key={item.value}>
            <ListItem
              disablePadding
              classes={{
                root: styles.root,
                selected: styles.selected,
              }}
              selected={!isChildrenElement && currentUrl === item.value}
            >
              <ListItemButton
                onClick={
                  item.children
                    ? () => handleClick(index)
                    : () => handleRedirect(item.value, parentItem)
                }
                style={{ paddingLeft: isChildrenElement && "53px", paddingTop: isChildrenElement && "0px" }}
              >
                {!isChildrenElement && (
                  <ListItemIcon style={{minWidth: '35px'}}>
                    {getIcon(item.value)}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.name}
                  className="operations-menu-item"
                />
              </ListItemButton>
            </ListItem>
            {item.children && (
              <>
                <Collapse in={open === index} timeout="auto" unmountOnExit>
                  <Divider />
                  <MyNestedList
                    items={item.children}
                    handleRedirect={handleRedirect}
                    level={level + 1}
                    isChildrenElement={true}
                    parentItem={item}
                  />
                </Collapse>
              </>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  const drawer = (
    <div>
      <Toolbar
        style={{
          paddingLeft: "0px",
          backgroundColor: '#f04a23',
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: 'fit-content'
        }}
      >
        <div style={{ padding: "13px 20px", width: drawerWidth, display: 'flex', alignItems: 'center' }}>
          <img
            style={{ width: "-webkit-fill-available", cursor: "pointer" }}
            src="/genie-logo.png"
            alt="Logo"
            onClick={() => navigate("/projects")}
          />
        </div>
        {/* <div>
          <span style={{color: 'white', fontWeight: '800'}}>{projectState.projectName || localStorage.getItem("projectName")}</span>
        </div> */}
      </Toolbar>
      <Divider />      
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "93vh",
          maxHeight: "93vh",
        }}
      >
        <div>
          <h4 style={{ padding: "10px 0px 10px 0px", backgroundColor:'#F04A23', margin: '0', color: 'white', textAlign:'center', fontSize: '15px' }}>OPERATIONS</h4>
          <Divider />
          <List disablePadding>
            <MyNestedList
              items={options}
              handleRedirect={handleRedirect}
              level={0}
            />
          </List>
        </div>
        <div>
        <h4 style={{ padding: "10px 0px 10px 0px", backgroundColor: '#F04A23', margin: '0', color: 'white', textAlign: 'center', fontSize: '15px' }}>OPTIONS</h4>
          <Divider />
          <List disablePadding>
            {/* <MyNestedList items={options} handleRedirect={handleRedirect} level={0} /> */}
            <ListItem
              disablePadding
              classes={{
                root: styles.root,
                selected: styles.selected,
              }}
              selected={currentUrl === "logs"}
            >
              <ListItemButton
                onClick={() => {
                  navigate(`/${project_type}/options/${projectId}/logs`);
                  setMobileOpen(false);
                }}
              >
                {
                  <ListItemIcon style={{minWidth: '35px'}}>
                    {getIcon('logs')}
                  </ListItemIcon>
                }
                <ListItemText primary="Logs" className="tasks-menu-item" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(`/${project_type}/options/${projectId}/upload-data`);
                  setMobileOpen(false);
                }}
              >
                {
                  <ListItemIcon style={{minWidth: '35px'}}>
                    {getIcon('upload-data')}
                  </ListItemIcon>
                }
                <ListItemText
                  primary="Upload Data"
                  className="tasks-menu-item"
                />
              </ListItemButton>
            </ListItem>
            {/* <ListItem disablePadding>
              <ListItemButton>
                {
                  <ListItemIcon>
                    <InboxIcon />
                  </ListItemIcon>
                }
                <ListItemText
                  primary="Data Stats"
                  className="tasks-menu-item"
                />
              </ListItemButton>
            </ListItem> */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(`/${project_type}/options/${projectId}/labels`);
                  setMobileOpen(false);
                }}
              >
                {
                  <ListItemIcon style={{minWidth: '35px'}}>
                    {getIcon('labels')}
                  </ListItemIcon>
                }
                <ListItemText primary="Labels" className="tasks-menu-item" />
              </ListItemButton>
            </ListItem>
            {project_type === '3d-ct_segmentation_-_(clarifier)' && (
              <ListItem
                disablePadding
                classes={{
                  root: styles.root,
                  selected: styles.selected,
                }}
                selected={currentUrl === "clarifier-history"}
              >
                <ListItemButton
                  onClick={() => {
                    navigate(`/${project_type}/options/${projectId}/clarifier-history`);
                    setMobileOpen(false);
                  }}
                >
                  {
                    <ListItemIcon style={{minWidth: '35px'}}>
                      {getIcon('clarifier-history')}
                    </ListItemIcon>
                  }
                  <ListItemText primary="Clarifier History" className="tasks-menu-item" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </div>
      </div>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          height: "75px",
          // backgroundImage: "linear-gradient(to right, #f04a23 , #ed8623a1)",
          backgroundColor: '#f04a23',
          boxShadow:
            "0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)",
        }}
      >
        <Toolbar style={{ height: "inherit" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            style={{ fontWeight: 900, color: "white", margin: "auto" }}
          >
            {currentPage}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          className="custom-sidebar"
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}
